import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { addExpense } from 'redux/actions';
import { Employee_Salary, Employee_Expense, Petty_Expense } from 'variables/expenseTypes';
import { VAT_RECIPROCAL } from 'variables/globalVAT';
import { standard } from 'variables/userAccountTypes';
import { months } from 'variables/months';

const ExpenseDetails = (props) => {

  const [typeInput, setTypeInput] = useState({firstLetter: "", type: "", vatable: false});
  const [employeeInput, setEmployeeInput] = useState({firstLetter: "", id: 0, displayId: "", name: ""});

  //if editing Expenses is enabled, the previous total(props.expenseInfo.total)
  //will contain VAT may need to be subtracted, update accordingly
  const [values, setValues] = useState({
    description: "",
    total: "",
    month: "0",
    year: moment().year().toString()
  });

  const typesObj = useMemo(() => {
    if( props.activeUserType === standard ) {
      return [
        { firstLetter: "P", type: Petty_Expense, vatable: false }
      ];
    }
    else {
      return props.expenseTypes
        .map(expenseType => {
          const firstLetter = expenseType.name[0].toUpperCase();
          return {firstLetter, type: expenseType.name, vatable: expenseType.vatable}
        })
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }

  }, [props.activeUserType, props.expenseTypes]);

  const employeesObj = useMemo(() => (
    props.employees
      .map(employee => {
        const firstLetter = employee.name[0].toUpperCase();
        return {firstLetter, id: employee.id, displayId: employee.displayId, name: employee.name}
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
    
  ), [props.employees]);

  const handleChange = (event) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value
    });
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const newExpense = {
      displayId: props.createExpenseDisplayId(),
      type: typeInput.type,
      description: values.description,
      total: values.total,
      ...( typeInput.vatable && {totalVat: values.total - Math.round( values.total / VAT_RECIPROCAL )} ),
      ...( typeInput.type === Employee_Expense && {employeeId: employeeInput.id} ),
      ...( typeInput.type === Employee_Salary && {monthYear: new Date(values.year, values.month), employeeId: employeeInput.id} ),
    };
    props.addExpense(newExpense, props.activeUserId, props.user.adminId);
    
    if(typeInput.type === Employee_Salary) {
      const invoiceInfo = {
        displayId: newExpense.displayId,
        employeeDisplayId: employeeInput.displayId,
        employeeName: employeeInput.name,
        monthYear: newExpense.monthYear,
        paymentDate: new Date(),
        description: values.description,
        total: values.total
      };
      props.openModal(invoiceInfo);
    }

    expenseCleanup();
  };

  const expenseCleanup = () => {
    setTypeInput({firstLetter: "", type: "", vatable: false});
    setEmployeeInput({firstLetter: "", id: 0, displayId: "", name: ""});
    setValues({
      description: "",
      total: "",
      month: "0",
      year: ""
    });

    //if Cancelled or Completed, redirect to ExpensesTable.
    props.setTabValue(0);
  };

  const getRequiredFields = (expenseType) => {
    switch(expenseType) {
      case Employee_Salary:
        return ['employee', 'monthYear'];
      case Employee_Expense:
        return ['employee'];
      default:
        return [];
    };
  };

  const conditionalRender = {
    employee: (
      <Grid
        item
        md={6}
        xs={12}
        key={"employee"}
      >
        <Autocomplete
          onChange={(event, newValue) => {
            if(newValue) {
              setEmployeeInput(newValue);
            }
            else {
              setEmployeeInput({firstLetter: "", id: 0, displayId: "", name: ""});
            }
          }}
          id="free-solo-dialog-demo"
          options={employeesObj}
          groupBy={(employee) => employee.firstLetter}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Select Employee.."
              variant="outlined"
              helperText="Please Select the Employee"
            />
          )}
          value={employeeInput ? ( employeeInput.id ? employeeInput : null ) : null }
        />
      </Grid>
    ),
    monthYear: (
      <React.Fragment key={"monthYear"}>
        <Grid
          item
          md={6}
          xs={12}
          key={"month"}
        >
          <TextField
            fullWidth
            helperText="Please select the Salary Month"
            label="Select Month"
            name="month"
            onChange={handleChange}
            select
            SelectProps={{ native: true }}
            value={values.month}
            variant="outlined"
          >
            {months.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid
          item
          md={6}
          xs={12}
          key={"year"}
        >
          <TextField
            fullWidth
            helperText="Please specify the Year"
            label="Year"
            name="year"
            onChange={handleChange}
            required
            value={values.year}
            variant="outlined"
          />
        </Grid>
      </React.Fragment>
    )
  };

  const requiredFields = getRequiredFields(typeInput.type);

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Card>
        <CardHeader
          subheader="This will add a New Expense."
          title="New Expense"
        />
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={6}
              xs={12}
            >
              <Autocomplete
                onChange={(event, newValue) => {
                  if(newValue) {
                    setTypeInput(newValue);
                  }
                  else {
                    setTypeInput({firstLetter: "", type: "", vatable: false});
                  }
                }}
                id="free-solo-dialog-demo"
                options={typesObj}
                groupBy={(expenseType) => expenseType.firstLetter}
                getOptionSelected={(option, value) => option.type === value.type}
                getOptionLabel={(option) => option.type}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Type.."
                    variant="outlined"
                    helperText="Please Select the Expense Type"
                  />
                )}
                value={typeInput ? ( typeInput.type ? typeInput : null ) : null }
              />
            </Grid>
            { requiredFields.map(field => conditionalRender[field]) }
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                helperText="Please Describe the Expense"
                label="Description"
                name="description"
                onChange={handleChange}
                required
                value={values.description}
                variant="outlined"
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                helperText="Please specify the Expense Amount (VAT Included)"
                label="Total Amount"
                onChange={(event) => setValues({ ...values, total: parseFloat(event.target.value) || 0 })}
                required
                value={values.total}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 2
          }}
        >
          <Button
            color="primary"
            variant="contained"
            type="submit"
          >
            Save details
          </Button>
          <Button sx={{ mx: 1 }} onClick={expenseCleanup}>
            Cancel
          </Button>
        </Box>
      </Card>
    </form>
  )
};

export default connect (null, {addExpense}) (ExpenseDetails);